import React, { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/components/ui/popover";
import { Checkbox } from "@/ui/components/ui/checkbox";
import { Input } from "@/ui/components/ui/input";
import { useGetAllPolygonsForSelector } from "../api/queries/polygon-selector.queries";
import { Loader2, Search, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { stringToColor } from "@/shared/utils/color/string-to-color";

interface PolygonSelectorProps {
  onSelectionChange: (ids: number[]) => void;
  selectedPolygonIds: number[];
  locationId?: number;
}

const PolygonSelector: React.FC<PolygonSelectorProps> = ({ onSelectionChange, selectedPolygonIds, locationId }) => {
  const { t } = useTranslation("polygon");
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const { data: polygons, isLoading, error } = useGetAllPolygonsForSelector(locationId);

  const filteredPolygons = useMemo(() => {
    if (!polygons) return [];
    if (!searchQuery.trim()) return polygons;
    return polygons.filter(polygon =>
      polygon.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [polygons, searchQuery]);

  const handleToggle = (polygonId: number) => {
    if (selectedPolygonIds.includes(polygonId)) {
      onSelectionChange(selectedPolygonIds.filter(id => id !== polygonId));
    } else {
      onSelectionChange([...selectedPolygonIds, polygonId]);
    }
  };

  const selectedCount = selectedPolygonIds.length;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm text-muted-foreground">{t("selector.loading")}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-500">
        {t("selector.loadError", { message: error.message })}
      </div>
    );
  }

  return (
    <div className="flex flex-row gap-4 items-center">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className="flex items-center justify-between w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <span className="truncate text-left">
              {selectedCount === 0
                ? t("selector.placeholder")
                : t("selector.selected", { count: selectedCount })}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="start">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("selector.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8"
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto p-1">
            {filteredPolygons.length === 0 && searchQuery && (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">
                {t("selector.noResults")}
              </div>
            )}
            {filteredPolygons.length === 0 && !searchQuery && (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">
                {t("selector.empty")}
              </div>
            )}
            {filteredPolygons.map((polygon) => (
              <label
                key={polygon.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-accent cursor-pointer text-sm"
              >
                <Checkbox
                  checked={selectedPolygonIds.includes(polygon.id)}
                  onCheckedChange={() => handleToggle(polygon.id)}
                />
                <div
                  className="h-3 w-3 rounded-full shrink-0 border border-border"
                  style={{ backgroundColor: stringToColor(polygon.name) }}
                />
                <span className="truncate">{polygon.name}</span>
              </label>
            ))}
          </div>
          {selectedCount > 0 && (
            <div className="border-t p-2">
              <button
                className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => onSelectionChange([])}
              >
                {t("selector.clearAll")}
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default PolygonSelector;
